import type { ServerRating } from '../api/missions';
import type { PlayableServer } from '../api/servers';

/**
 * Whether a server can run a mission, and how sure the platform is about it.
 *
 * <p>A mission is made for what one server ran on the day it was made, and it
 * keeps that list (`definition.modSet`): Workshop mods and Creator DLCs. The
 * server is edited on the panel afterwards, or deleted, and the question before
 * a night is which servers can still run it (the owner, 2026-09-19). It used to
 * be asked against a panel preset, which on this panel is a Launcher file
 * nobody edits; the mission's own list is what it is asked against now.
 *
 * <p>The platform answers it exactly where it can ({@link withRating}): its
 * catalogue of what a server runs lists every class an editor can place, so a
 * server is rated by whether it has the mission's world and every unit, vehicle
 * and object class the mission names. Comparing mod lists, below, is what is
 * left when that answer is not there.
 *
 * <ul>
 * <li><b>exact</b>: the same mod set hash, or every mod of the mission's list
 * present. Nothing to say.</li>
 * <li><b>partial</b>: the terrain is there and some of the mission's mods are
 * not. It may still play — a stable branch for a development one keeps its
 * class names — and the picker says what is missing so a person can judge,
 * until the class-level check can say it for them.</li>
 * <li><b>no</b>: the terrain's own mod is not on the server. Not selectable.</li>
 * </ul>
 */
export type Fit = 'exact' | 'partial' | 'no';

export interface ServerFit {
  fit: Fit;
  /** The mission's mods this server does not carry, by name, for the label's title. */
  missing: string[];
  /** The mission's classes what the server runs has not got, when the platform rated it class by class. */
  classes?: string[];
  /** Nobody has read what the server runs now: it may play the mission, and nothing says so. */
  unread?: boolean;
}

/**
 * The platform's class-by-class rating laid over the mod-list one. `same` and
 * `compatible` are both "plays it as it is", whatever mods differ; `changes`
 * names the classes; `unknown` keeps the mod-list guess and says it is one.
 */
export function withRating(fit: ServerFit, rating: ServerRating | undefined): ServerFit {
  if (!rating) return fit;
  switch (rating.level) {
    case 'same':
    case 'compatible':
      return { fit: 'exact', missing: [], classes: [] };
    case 'changes':
      return { fit: 'partial', missing: fit.missing, classes: rating.missingClasses };
    case 'no_terrain':
      return { fit: 'no', missing: [] };
    default:
      return { ...fit, fit: fit.fit === 'no' ? 'no' : 'partial', unread: true };
  }
}

/** One mod of a mission's own list, as its definition keeps it. */
export interface MissionMod {
  workshopId: number;
  name: string;
}

export function fitOf(
  server: PlayableServer,
  missionMods: MissionMod[],
  terrainModId: number | null,
  missionHash?: string | null,
): ServerFit {
  // The same mod set, hashed the same way: nothing else needs checking.
  if (missionHash && server.modSetHash && server.modSetHash === missionHash) {
    return { fit: 'exact', missing: [] };
  }
  const ids = new Set(
    (server.activeMods ?? []).map((m) => m.id).filter((id) => typeof id === 'number'),
  );
  // The panel has not read this server's mods: that is not evidence of anything,
  // and refusing a server for a fact nobody has is worse than letting it through.
  if (ids.size === 0) return { fit: 'partial', missing: [] };

  if (terrainModId != null && !ids.has(terrainModId)) return { fit: 'no', missing: [] };

  if (missionMods.length === 0) {
    // No list to compare against: the hashes differing is all there is to say.
    const differs = Boolean(missionHash && server.modSetHash && server.modSetHash !== missionHash);
    return { fit: differs ? 'partial' : 'exact', missing: [] };
  }

  const missing = missionMods.filter((m) => !ids.has(m.workshopId)).map((m) => m.name);
  return missing.length === 0 ? { fit: 'exact', missing: [] } : { fit: 'partial', missing };
}

/**
 * Whether the platform may start a session on a server at all: one it made
 * (MANAGED) or an operator's that an admin linked (LINKED). A server the panel
 * lists with no access is still the group's, and a picker shows it, greyed and
 * with the way to link it, rather than hiding it: hiding them is how the
 * group's own six servers vanished from the list (QUICK_START_REVIEW.md F7).
 */
export function usable(server: Pick<PlayableServer, 'access'>): boolean {
  return server.access === 'MANAGED' || server.access === 'LINKED';
}

/**
 * The best server to propose when nothing says which: one the platform may use
 * and that can certainly run the mission; among equals one that is already up,
 * because a running server is a session that starts now instead of in four
 * minutes; and among the ones that are only nearly right, the group's own
 * linked server before one the platform made.
 */
export function preferred<T extends { server: PlayableServer; fit: Fit }>(rows: T[]): T | null {
  const rank = (row: T) =>
    (row.fit === 'exact' ? 0 : 4) +
    (row.fit === 'partial' && row.server.access !== 'LINKED' ? 2 : 0) +
    (row.server.running ? 0 : 1);
  return (
    rows.filter((r) => r.fit !== 'no' && usable(r.server)).sort((a, b) => rank(a) - rank(b))[0] ??
    null
  );
}

/**
 * A server name without the web address a host appends to it: "Name - www.example.cat"
 * shows as "Name". The full name stays in the card's title.
 */
export function shortServerName(name: string): string {
  const short = name.replace(/\s+[-–—]\s+www\.\S*\s*$/i, '').trim();
  return short || name;
}
